import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';

const PlaylistVideosPage = ({ token }) => {
  const { playlistId } = useParams();
  const [videos, setVideos] = useState([]);
  const [playlistTitle, setPlaylistTitle] = useState('');
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const playerRef = useRef(null);  // Reference for the YouTube Player instance
  const isPlayerReady = useRef(false);  // Track if the player is ready

  // Load the YouTube Iframe API if it's not already loaded
  const loadYouTubeIframeAPI = () => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  };

  useEffect(() => {
    const fetchPlaylistVideos = async () => {
      const response = await axios.get('https://www.googleapis.com/youtube/v3/playlistItems', {
        params: {
          part: 'snippet',
          playlistId: playlistId,
          maxResults: 50,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVideos(response.data.items);
      if (response.data.items.length > 0) {
        setPlaylistTitle(response.data.items[0].snippet.playlistTitle);
      }
    };

    fetchPlaylistVideos();
    loadYouTubeIframeAPI();  // Load the YouTube API
  }, [playlistId, token]);

  // Function to handle the video click and open the modal
  const handleVideoClick = (index) => {
    setSelectedVideoIndex(index);
    setShowModal(true);
  };

  // Function to handle closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
    if (playerRef.current) {
      playerRef.current.stopVideo();  // Stop video when modal is closed
    }
  };

  // Load the next video in the playlist
  const loadNextVideo = () => {
    if (selectedVideoIndex < videos.length - 1) {
      setSelectedVideoIndex(selectedVideoIndex + 1);
    } else {
      setShowModal(false);  // Close modal if it's the last video
    }
  };

  // Initialize the YouTube Player when the modal opens
  useEffect(() => {
    const onYouTubeIframeAPIReady = () => {
      try {
        if (showModal && !playerRef.current) {
          playerRef.current = new window.YT.Player('player', {
            events: {
              'onReady': (event) => {
                isPlayerReady.current = true;  // Mark the player as ready
                loadAndPlayCurrentVideo();  // Load and play the current video
              },
              'onStateChange': (event) => {
                if (event.data === window.YT.PlayerState.ENDED) {
                  loadNextVideo();  // Play the next video when the current one ends
                }
              },
              'onError': (error) => {
                console.error('YouTube Player error:', error);
                loadNextVideo();  // Skip to the next video if there's an error
              },
            },
          });
        } else if (playerRef.current && isPlayerReady.current) {
          // If player already exists and is ready, load and play the video
          loadAndPlayCurrentVideo();
        }
      } catch (error) {
        console.error('Error initializing YouTube Player:', error);
        resetPlayer();
      }
    };

    // Check if the API is ready, if not, wait until it's loaded
    if (window.YT && window.YT.Player) {
      onYouTubeIframeAPIReady();
    } else {
      window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
    }
  }, [showModal, selectedVideoIndex, videos]);

  // Load the current video and play it
  const loadAndPlayCurrentVideo = () => {
    try {
      if (playerRef.current && videos[selectedVideoIndex] && isPlayerReady.current) {
        const videoId = videos[selectedVideoIndex].snippet.resourceId.videoId;
        playerRef.current.loadVideoById(videoId);  // Load the next video by ID
        playerRef.current.playVideo();  // Autoplay the video
        playerRef.current.unMute();  // Ensure the sound is enabled
      }
    } catch (error) {
      console.error('Error loading and playing the video:', error);
      loadNextVideo();  // Skip to the next video if an error occurs
    }
  };

  // Reset player if it gets into an inconsistent state
  const resetPlayer = () => {
    console.warn('Resetting YouTube Player');
    playerRef.current = null;  // Clear the reference
    isPlayerReady.current = false;  // Reset the player ready state
    if (document.getElementById('player')) {
      document.getElementById('player').innerHTML = '';  // Clear out the previous player
    }
  };

  return (
    <div className="container">
      <h2 className="my-4">{playlistTitle}</h2>
      <Link to="/playlists" className="btn btn-secondary mb-4">Back to Playlists</Link>
      <div className="row">
        {videos.map((video, index) => (
          <div className="col-md-3 col-sm-6 mb-4" key={video.snippet.resourceId.videoId}>
            <div className="card" onClick={() => handleVideoClick(index)}>
              {video.snippet.thumbnails && video.snippet.thumbnails.high ? (
                <img
                  src={video.snippet.thumbnails.high.url}
                  className="card-img-top"
                  alt={video.snippet.title}
                  style={{ width: '66%', height: 'auto', margin: '0 auto' }}
                />
              ) : (
                <div className="no-thumbnail">No Thumbnail Available</div>
              )}
              <div className="card-body">
                <h5 className="card-title text-center">{video.snippet.title}</h5>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal for playing video */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Now Playing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {videos.length > 0 && (
            <div className="video-container" style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <div id="player" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}></div>  {/* Expanded player */}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PlaylistVideosPage;
